import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { OmnisearchResults, OmnisearchEntityKey } from '@/api/omnisearchApi'

// ─── Historique localStorage ──────────────────────────────────────────────────

const HISTORY_KEY = 'omnisearch:history'
const HISTORY_MAX = 8

function loadHistory(): string[] {
  try {
    return JSON.parse(localStorage.getItem(HISTORY_KEY) ?? '[]')
  } catch {
    return []
  }
}

function saveHistory(history: string[]) {
  localStorage.setItem(HISTORY_KEY, JSON.stringify(history))
}

// ─── Store ────────────────────────────────────────────────────────────────────

export const useOmnisearchStore = defineStore('omnisearch', () => {
  // ── État overlay ──────────────────────────────────────────────────────────
  const isOpen = ref(false)

  // ── Requête ───────────────────────────────────────────────────────────────
  const query = ref('')

  // ── Filtre actif (null = tout afficher) ───────────────────────────────────
  const activeScope = ref<OmnisearchEntityKey | null>(null)

  // ── Résultats ─────────────────────────────────────────────────────────────
  const results = ref<OmnisearchResults | null>(null)
  const isLoading = ref(false)
  const hasError = ref(false)

  // ── Historique ────────────────────────────────────────────────────────────
  const recentSearches = ref<string[]>(loadHistory())

  // ── Computed ──────────────────────────────────────────────────────────────
  const totalResults = computed(() => {
    if (!results.value) return 0
    return Object.values(results.value).reduce((sum, arr) => sum + arr.length, 0)
  })

  const hasResults = computed(() => totalResults.value > 0)

  // ── Actions ───────────────────────────────────────────────────────────────
  function open() {
    isOpen.value = true
  }

  function close() {
    isOpen.value = false
    query.value = ''
    results.value = null
    activeScope.value = null
    hasError.value = false
  }

  function toggle() {
    isOpen.value ? close() : open()
  }

  function setQuery(q: string) {
    query.value = q
  }

  function setResults(r: OmnisearchResults) {
    results.value = r
    hasError.value = false
  }

  function setLoading(v: boolean) {
    isLoading.value = v
  }

  function setError(v: boolean) {
    hasError.value = v
    if (v) isLoading.value = false
  }

  function setScope(scope: OmnisearchEntityKey | null) {
    activeScope.value = scope
  }

  function pushHistory(term: string) {
    const trimmed = term.trim()
    if (!trimmed) return
    const filtered = recentSearches.value.filter((s) => s !== trimmed)
    recentSearches.value = [trimmed, ...filtered].slice(0, HISTORY_MAX)
    saveHistory(recentSearches.value)
  }

  function clearHistory() {
    recentSearches.value = []
    saveHistory([])
  }

  return {
    // state
    isOpen,
    query,
    activeScope,
    results,
    isLoading,
    hasError,
    recentSearches,
    // computed
    totalResults,
    hasResults,
    // actions
    open,
    close,
    toggle,
    setQuery,
    setResults,
    setLoading,
    setError,
    setScope,
    pushHistory,
    clearHistory,
  }
})
